import React, { useEffect, useMemo, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import type { AutomationField, TemplateAutomation, WaAutomationSettings, WaTemplate } from "@/types/whatsapp";

const FIELD_LABELS: Record<AutomationField, string> = {
  contact_name: "Contact name",
  company_name: "Buyer's company",
  deal_name: "Deal / enquiry name",
  product_summary: "Items (first product with quantity)",
  quote_number: "Quotation number",
  quote_amount: "Quotation amount",
  valid_until: "Valid until",
  rep_name: "Executive's name",
  order_number: "Order number",
  po_number: "Buyer's PO number",
  order_value: "Order value",
  delivery_date: "Delivery date (earliest)",
  sample_number: "Sample number",
  sample_item: "Sample item and shade",
  courier: "Courier",
  awb_number: "AWB / docket number",
  dispatch_number: "Dispatch number",
  dispatch_items: "Items sent (first with quantity)",
  transporter: "Transporter",
  lr_number: "LR number",
  packages: "Rolls / bales",
  invoice_number: "Invoice number",
  complaint_number: "Complaint number",
  complaint_type: "Complaint type",
};

/** Which fields each automation can fill; the server checks the same lists */
const QUOTE_FIELDS: AutomationField[] = ["contact_name", "company_name", "deal_name", "product_summary", "quote_number", "quote_amount", "valid_until", "rep_name"];
const ORDER_FIELDS: AutomationField[] = ["contact_name", "company_name", "order_number", "po_number", "order_value", "product_summary", "delivery_date", "rep_name"];
const SAMPLE_FIELDS: AutomationField[] = ["contact_name", "company_name", "sample_number", "sample_item", "courier", "awb_number", "rep_name"];
const DISPATCH_FIELDS: AutomationField[] = ["contact_name", "company_name", "order_number", "po_number", "dispatch_number", "dispatch_items", "transporter", "lr_number", "packages", "invoice_number", "rep_name"];
const COMPLAINT_FIELDS: AutomationField[] = ["contact_name", "company_name", "complaint_number", "complaint_type", "order_number", "rep_name"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Admin: how WhatsApp behaves on its own */
export function AutomationSettings({ connected = true }: { connected?: boolean }) {
  const { success, error } = useToast();
  const [settings, setSettings] = useState<WaAutomationSettings | null>(null);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/api/WhatsApp/Automation")
      .then((res) => setSettings(unwrapObject<{ settings: WaAutomationSettings }>(res).settings))
      .catch((err) => error("Could not load automation settings", err.message));
    api
      .get("/api/WhatsApp/Templates")
      .then((res) => setTemplates(unwrapArray<WaTemplate>(res)))
      .catch(() => setTemplates([]));
  }, [error]);

  if (!settings) {
    return (
      <div className="bg-card border rounded-2xl p-6 flex justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const set = <K extends keyof WaAutomationSettings>(k: K, v: WaAutomationSettings[K]) => setSettings((s) => (s ? { ...s, [k]: v } : s));

  const save = async () => {
    setSaving(true);
    try {
      const res = unwrapObject<{ settings: WaAutomationSettings }>(await api.put("/api/WhatsApp/Automation", { settings }));
      setSettings(res.settings);
      success("Automation saved");
    } catch (err: any) {
      error("Could not save", err.message);
    } finally {
      setSaving(false);
    }
  };

  const ah = settings.after_hours;

  return (
    <div className="bg-card border rounded-2xl p-5 space-y-6 max-w-6xl">
      <div>
        <h2 className="text-sm font-bold">Automation</h2>
        <p className="text-xs text-muted-foreground mt-0.5">What WhatsApp does on its own. Changes apply to new messages.</p>
      </div>

      {!connected && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          Connect the company WhatsApp number on the Connection tab first. You can review these options now; saving is enabled once it's connected.
        </div>
      )}

      <Section title="New chats from unknown numbers">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Who handles them">
            <Select value={settings.unassigned_routing} onValueChange={(v) => set("unassigned_routing", v as any)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="queue">Shared queue — an executive clicks "Assign to me"</SelectItem>
                <SelectItem value="round_robin">Round-robin — next executive in turn (lead assignment order)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Who gets a notification while unassigned">
            <Select value={settings.unassigned_notify} onValueChange={(v) => set("unassigned_notify", v as any)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="heads">Sales heads and admins</SelectItem>
                <SelectItem value="all">Everyone (all executives, heads and admins)</SelectItem>
                <SelectItem value="none">Nobody (sidebar badge only)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      <Section
        title="After-hours auto-reply"
        toggle={<Switch checked={ah.enabled} onCheckedChange={(v) => set("after_hours", { ...ah, enabled: v })} />}
        hint="Sent once per buyer per 12 hours when they message outside working hours."
      >
        <div className="grid sm:grid-cols-4 gap-3">
          <Field label="Working from">
            <Input type="time" value={ah.start} onChange={(e) => set("after_hours", { ...ah, start: e.target.value })} />
          </Field>
          <Field label="Until">
            <Input type="time" value={ah.end} onChange={(e) => set("after_hours", { ...ah, end: e.target.value })} />
          </Field>
          <Field label="Time zone" className="sm:col-span-2">
            <Input value={ah.timezone} onChange={(e) => set("after_hours", { ...ah, timezone: e.target.value })} placeholder="Asia/Kolkata" />
          </Field>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map((d, i) => {
            const on = ah.days.includes(i);
            return (
              <button
                key={d}
                type="button"
                onClick={() => set("after_hours", { ...ah, days: on ? ah.days.filter((x) => x !== i) : [...ah.days, i].sort() })}
                className={`px-3 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground"}`}
              >
                {d}
              </button>
            );
          })}
          <span className="text-[11px] text-muted-foreground self-center ml-1">Working days</span>
        </div>
        <Field label="Message">
          <Textarea rows={2} value={ah.message} maxLength={1000} onChange={(e) => set("after_hours", { ...ah, message: e.target.value })} />
        </Field>
      </Section>

      <TemplateAutomationEditor
        title="When a quotation is emailed, also send it on WhatsApp"
        hint="Uses an approved template; a template with a document header gets the quotation PDF attached."
        value={settings.quote_sent}
        fields={QUOTE_FIELDS}
        templates={templates}
        onChange={(v) => set("quote_sent", v)}
      />

      <TemplateAutomationEditor
        title="Remind the buyer before a sent quotation expires"
        hint="Runs daily at 10:00 for quotations in Sent status. Each quotation is reminded once."
        value={settings.quote_expiry}
        fields={QUOTE_FIELDS}
        templates={templates}
        onChange={(v) => set("quote_expiry", { ...settings.quote_expiry, ...v })}
        extra={
          <Field label="Days before expiry">
            <Input
              type="number"
              min={1}
              max={30}
              value={settings.quote_expiry.days_before}
              onChange={(e) => set("quote_expiry", { ...settings.quote_expiry, days_before: Number(e.target.value) })}
            />
          </Field>
        }
      />

      <TemplateAutomationEditor
        title="When an order is made, send the buyer the order confirmation"
        hint="A template with a document header gets the order confirmation PDF attached. Sent once per order."
        value={settings.order_confirmed}
        fields={ORDER_FIELDS}
        templates={templates}
        onChange={(v) => set("order_confirmed", v)}
      />

      <TemplateAutomationEditor
        title="When a sample is dispatched, send the courier and AWB"
        hint="Sent once for each dispatch of a sample, so a fresh round after a revision goes too."
        value={settings.sample_dispatched}
        fields={SAMPLE_FIELDS}
        templates={templates}
        allowDocument={false}
        onChange={(v) => set("sample_dispatched", v)}
      />

      <TemplateAutomationEditor
        title="When goods are dispatched, send the transporter and LR number"
        hint="A template with a document header gets the packing list PDF attached. Sent once per dispatch."
        value={settings.goods_dispatched}
        fields={DISPATCH_FIELDS}
        templates={templates}
        onChange={(v) => set("goods_dispatched", v)}
      />

      <TemplateAutomationEditor
        title="When a complaint is logged, acknowledge it with its number"
        hint="Tells the buyer the complaint is registered and gives them its number to quote."
        value={settings.complaint_received}
        fields={COMPLAINT_FIELDS}
        templates={templates}
        allowDocument={false}
        onChange={(v) => set("complaint_received", v)}
      />

      <p className="text-[11px] text-muted-foreground">
        These go to the deal's main contact, or else the buyer's primary contact with a mobile number. Nothing is sent to a buyer who replied STOP.
      </p>

      <Section title="Cost estimate (₹ per charged message)" hint="Meta charges template messages by category. Enter today's rates from Meta's price list for India.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(["marketing", "utility", "authentication", "service"] as const).map((k) => (
            <Field key={k} label={k[0]!.toUpperCase() + k.slice(1)}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={settings.rates[k]}
                onChange={(e) => set("rates", { ...settings.rates, [k]: Number(e.target.value) })}
              />
            </Field>
          ))}
        </div>
      </Section>

      <Section title="Media retention" hint="Photos, voice notes and documents older than this are deleted from the server every night. Messages stay. 0 keeps files forever.">
        <div className="max-w-xs">
          <Field label="Keep files for (days)">
            <Input type="number" min={0} max={3650} value={settings.media_retention_days} onChange={(e) => set("media_retention_days", Number(e.target.value))} />
          </Field>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !connected}>
          {saving && <Loader2 className="size-4 animate-spin mr-1.5" />}
          Save automation
        </Button>
      </div>
    </div>
  );
}

function Section({ title, hint, toggle, children }: { title: string; hint?: string; toggle?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
        {toggle}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1 ${className || ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

/** Pick a template and map each of its variables to a CRM field */
function TemplateAutomationEditor({
  title,
  hint,
  value,
  fields,
  templates,
  onChange,
  extra,
  allowDocument = true,
}: {
  title: string;
  hint: string;
  value: TemplateAutomation;
  fields: AutomationField[];
  allowDocument?: boolean;
  templates: WaTemplate[];
  onChange: (v: TemplateAutomation) => void;
  extra?: React.ReactNode;
}) {
  const t = templates.find((x) => x.id === value.template_id);
  const variables = useMemo(() => (t ? [...t.header_variables.map((v) => `Header {{${v}}}`), ...t.body_variables.map((v) => `{{${v}}}`)] : []), [t]);
  const usable = templates.filter((x) => !["IMAGE", "VIDEO", ...(allowDocument ? [] : ["DOCUMENT"])].includes(x.header_format || ""));

  return (
    <Section title={title} hint={hint} toggle={<Switch checked={value.enabled} onCheckedChange={(v) => onChange({ ...value, enabled: v })} />}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Template">
          <Select
            value={value.template_id || ""}
            onValueChange={(id) => {
              const tpl = templates.find((x) => x.id === id);
              const count = (tpl?.header_variables.length || 0) + (tpl?.body_variables.length || 0);
              onChange({ ...value, template_id: id, params: Array.from({ length: count }, (_, i) => value.params[i] || "contact_name") });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={usable.length ? "Choose an approved template" : "No approved templates yet"} />
            </SelectTrigger>
            <SelectContent>
              {usable.map((x) => (
                <SelectItem key={x.id} value={x.id}>
                  {x.name} ({x.language}){x.header_format === "DOCUMENT" ? " · with PDF" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {extra}
      </div>
      {t && (
        <div className="rounded-lg bg-muted/40 p-3 space-y-2">
          <p className="text-xs text-muted-foreground whitespace-pre-wrap">{t.body_text}</p>
          {variables.map((label, i) => (
            <div key={label} className="flex items-center gap-2 text-xs">
              <span className="w-24 shrink-0 font-mono">{label}</span>
              <Select
                value={value.params[i] || "contact_name"}
                onValueChange={(f) => onChange({ ...value, params: variables.map((_, idx) => (idx === i ? (f as AutomationField) : value.params[idx] || "contact_name")) })}
              >
                <SelectTrigger className="h-8 text-xs w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((f) => (
                    <SelectItem key={f} value={f} className="text-xs">
                      {FIELD_LABELS[f]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
